<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :actions="actions"
            :refresh.sync="refreshTable"
            ajax-path="sip/gateways"
            @rowSelected="handleRowSelected"
        />
        <app-modal
            width="lg:w-1/3"
            :show.sync="showAddModal"
        >
            <template slot="header">Add Rule</template>
            <template slot="body">
                <app-input
                    v-model="addForm.address"
                    placeholder="IP Address"
                    :error="addForm.errors.get('address')"
                    @updated="addForm.errors.clear('address')"
                    focus
                    required
                />
                <app-input
                    v-model="addForm.description"
                    placeholder="Description"
                    :error="addForm.errors.get('description')"
                    @updated="addForm.errors.clear('description')"
                    required
                />
                <app-input
                    v-model="addForm.attrs"
                    placeholder="Attributes"
                    :error="addForm.errors.get('attrs')"
                    @updated="addForm.errors.clear('attrs')"
                />                
                <div class="text-right mt-5">
                    <app-button class="btn-white"
                        @click="showAddModal = false"
                    >Cancel</app-button>
                    <app-button
                        class="btn-blue ml-3"
                        :loading="adding"
                        @click="store"
                    >Add</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            width="lg:w-1/3"
            :show.sync="showEditModal"
        >
            <template slot="header">Edit Rule</template>
            <template slot="body">
                <app-input
                    v-model="editForm.address"
                    placeholder="IP Address"
                    :error="editForm.errors.get('address')"
                    @updated="editForm.errors.clear('address')"
                    focus
                    required
                />
                <app-input
                    v-model="editForm.description"
                    placeholder="Description"
                    :error="editForm.errors.get('description')"
                    @updated="editForm.errors.clear('description')"
                    required
                />
                <app-input
                    v-model="editForm.attrs"
                    placeholder="Attributes"
                    :error="editForm.errors.get('attrs')"
                    @updated="editForm.errors.clear('attrs')"
                />                                
                <div class="text-right mt-5">
                    <app-button class="btn-white"
                        @click="showEditModal = false"
                    >Cancel</app-button>
                    <app-button
                        class="btn-blue ml-3"
                        :loading="updating"
                        @click="update"
                    >Update</app-button>
                </div>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Form from '@/helpers/Form';
    import debounce from 'lodash/debounce';
    
    export default {
        data() {
            return {
                showAddModal: false,
                showEditModal: false,
                adding: false,
                updating: false,
                refreshTable: false,
                addForm: new Form({
                    address: null,
                    attrs: null,
                    description: null,
                }),
                editForm: new Form({
                    address: null,
                    attrs: null,
                    description: null,
                }),
                columns: [
                    {
                        title: 'IP Address',
                        field: 'address',
                        sorter: 'string',
                        minWidth: 200,
                    },
                    {
                        title: 'Description',
                        field: 'description',
                        sorter: 'string',
                        minWidth: 250,
                    },
                    {
                        title: 'Attributes',
                        field: 'attrs',
                        sorter: 'string',
                        minWidth: 200,
                    },                   
                ],
                actions: [
                    {
                        label: 'Edit',
                        icon: () => import('@/icons/EditOutlined.vue'),
                        requiredSelect: true,
                        handler: gateways => {
                            this.$store.commit('sip/gateways/setSelected', gateways[0]);
                            this.showEditModal = true;
                            this.editForm.set(gateways[0]);
                        }
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        requiredSelect: true,
                        handler: gateways => {
                            this.$store.commit('sip/gateways/setSelected', gateways[0]);
                            this.delete(gateways[0]);
                        }
                    },
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: this.add,
                    }
                ]
            }
        },
        computed: {
            ...mapGetters({
                items: 'sip/gateways/items',
                selected: 'sip/gateways/selected',          
            }),
        },
        methods: {            
            add() {
                this.showAddModal = true;
                this.addForm.reset();
                this.addForm.errors.clear();
            },
            handleRowSelected(row) {
                this.$store.commit('sip/gateways/setSelected', row.data);
            },        
            store() {
                this.adding = true;
                this.addForm.errors.clear();
                this.$store.dispatch('sip/gateways/store', this.addForm.payload())
                    .then(() => {
                        this.adding = false;
                        this.showAddModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Gateway was successfully added.'
                        })
                    })
                    .catch(({ response }) => {
                        this.adding = false;
                        this.addForm.errors.record(response.data);
                    })
            },
            update() {
                this.updating = true;
                this.editForm.errors.clear();
                this.$store.dispatch('sip/gateways/update', this.editForm.payload())
                    .then(() => {
                        this.updating = false;
                        this.showEditModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Gateway was successfully updated.'
                        })
                    })
                    .catch(({ response }) => {
                        this.updating = false;
                        this.editForm.errors.record(response.data);
                    })
            },
            delete(gateway) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to delete gateway "<strong>${gateway.description}</strong>" permanently.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, delete it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('sip/gateways/delete');
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Gateway was successfully deleted.'
                        })
                    }
                })                
            },                           
        }
    }
</script>